import React, { useState, useEffect } from 'react';
import { Input, InputGroup, InputSuffix } from '@fiverr-private/forms';
import { TextButton } from '@fiverr-private/button';
import { Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import { translate, I18n } from '@fiverr-private/i18n-react';
import { LinkIcon } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { track as mixpanelTrack } from '../../../lib/mixpanel';
import { sendEvent as sendBQEvent } from '../../../lib/bigQuery';
import { SocialLinks } from '../../../lib/interfaces';
import { Socials } from '../Socials';
import { copyNotificationClass } from './styles.ve.css';

interface ShareLinkProps {
  inviteLink?: string;
  socialLinks: SocialLinks;
}

export const ShareLink = ({ inviteLink = '', socialLinks }: ShareLinkProps) => {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const copyToClipboard = async (textToCopy) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(textToCopy);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;

      textArea.style.position = 'absolute';
      textArea.style.left = '-999999px';

      document.body.prepend(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (error: any) {
        logger.warn(error, { description: 'Failed to copy to clipboard' });
      } finally {
        textArea.remove();
      }
    }
  };

  const onCopyClick = () => {
    if (inviteLink) {
      copyToClipboard(inviteLink);

      setCopySuccess(true);
      mixpanelTrack('Referral Page Inviter - Copy Link  Button - Click');

      const { userId } = getContext();

      sendBQEvent({
        type: 'clicked_on_copy_referral_invitation_link',
        referral_program: {
          referral_inviter: userId,
        },
        page: {
          element: {
            name: 'invitation_link',
            text: translate('referral_program_perseus.invitation.share.copy'),
            type: 'link',
          },
        },
        action: {
          type: 'click',
        },
      });
    }
  };

  useEffect(() => {
    if (!copySuccess) {
      return;
    }

    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  }, [copySuccess]);

  return (
    <div>
      <label htmlFor="copy-link">
        <Text color="bodyPrimary">
          <I18n k="referral_program_perseus.invitation.share.title" />
        </Text>
      </label>

      <Stack
        marginY="2"
        gap="4"
        direction={{ default: 'column', sm: 'row' }}
        alignItems={{ default: 'flexStart', sm: 'stretch' }}
        justifyContent="spaceBetween"
      >
        <Stack gap="2" width="100%" position="relative">
          <InputGroup flex="1 1">
            <Input readOnly name="copy-link" defaultValue={inviteLink} />
            <InputSuffix>
              <TextButton onClick={onCopyClick} size="sm">
                <LinkIcon />
                <I18n k="referral_program_perseus.invitation.share.copy" />
              </TextButton>
            </InputSuffix>
          </InputGroup>
          {copySuccess && (
            <span className={copyNotificationClass}>
              <I18n k="referral_program_perseus.invitation.share.copied" />
            </span>
          )}
        </Stack>
        <Socials socialLinks={socialLinks} />
      </Stack>
    </div>
  );
};
